<template>
  <div>
    <v-dialog
      max-width="720"
      v-model="isDialogOpen"
    >
      <v-card
        class="pa-4"
        v-if="formattedDate"
      >
        <page-title :value="`新規献立作成 - ${formattedDate}`" />

        <div class="my-4">
          <div class="category-header">
            <div class="font-weight-bold my-2">種別を選択</div>
            <v-spacer />
            <v-btn
              color="primary"
              variant="text"
              size="small"
              @click="openCreateCategoryDialog"
            >+ 種別を追加</v-btn>
          </div>
          <div>
            <div class="category-table-headers">
              <div class="category-table-header col-checkbox"></div>
              <div class="category-table-header col-name">種別名</div>
              <div class="category-table-header col-classes">対象クラス</div>
            </div>
            <div 
              class="category-table-row"
              v-for="(c, i) in menuCategories"
              :key="i"
              @click="categoryId = c.categoryId"
            >
              <div class="category-table-item col-checkbox">
                <v-icon v-if="categoryId === c.categoryId" color="primary">mdi-radiobox-marked</v-icon>
                <v-icon v-else>mdi-radiobox-blank</v-icon>
              </div>
              <div class="category-table-item col-name"> {{ c.name }} </div>
              <div class="category-table-item col-classes"> {{ formatClassNames(c.targetClasses) }} </div>
            </div>
          </div>
          <v-divider />
        </div>

        <div class="my-4">
          <v-textarea
            v-model="body"
            variant="outlined"
            label="献立"
            hide-details
            color="primary"
          />
        </div>
        <div class="my-4">
          <v-textarea
            v-model="note"
            variant="outlined"
            label="備考"
            hide-details
            color="primary"
          />
        </div>
        <div class="my-4">
          <v-select
            v-model="allergenIds"
            variant="outlined"
            hide-details
            label="アレルギー食品"
            :items="allergens"
            item-title="name"
            item-value="allergenId"
            multiple
            color="primary"
          />
        </div>

        <v-progress-linear v-if="isCreating" indeterminate color="primary" class="my-4" />

        <div class="my-4 d-flex align-center">
          <v-spacer />
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!creationValid || isCreating"
            @click="createMenu"
          >作成</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>

    <food-category-create-container />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { foodMenusModule } from '@/store/viewModules/food-menu/foodMenusModule';
import dayjs from 'dayjs';

import PageTitle from '@/components/atoms/PageTitle.vue';
import FoodCategoryCreateContainer from '@/containers/food-menu/FoodCategoryCreateContainer.vue';
import { NurseryClassResponse } from 'chaild-api/lib';

interface DataType {
  isCreating: boolean;

  categoryId: number | null;
  body: string;
  note: string;
  allergenIds: number[];
}

export default defineComponent({
  components: {
    PageTitle,
    FoodCategoryCreateContainer,
  },

  data: (): DataType => ({
    isCreating: false,

    categoryId: null,
    body: '',
    note: '',
    allergenIds: [],
  }),

  computed: {
    menuCategories() {
      return foodMenusModule.menuCategories;
    },
    allergens() {
      return foodMenusModule.allergens;
    },
    creatingDate() {
      return foodMenusModule.creatingDate;
    },
    formattedDate(): string | null {
      if (this.creatingDate) {
        return dayjs(this.creatingDate).format('MM/DD (ddd)');
      }
      return null;
    },
    isDialogOpen: {
      get(): boolean {
        return !!this.creatingDate;
      },
      set(val: boolean) {
        if (!val) {
          this.closeDialog();
        }
      }
    },
    creationValid(): boolean {
      if (
        this.categoryId
        && this.body.trim().length > 0
      ) {
        return true;
      }
      return false;
    }
  },

  methods: {
    async createMenu() {
      if (this.categoryId) {
        this.isCreating = true;
        await foodMenusModule.createMenu({
          categoryId: this.categoryId,
          body: this.body,
          note: this.note,
          allergenIds: this.allergenIds,
        });
        this.isCreating = false;
        this.closeDialog();
      }
    },
    clear() {
      this.categoryId = null;
      this.body = '';
      this.note = '';
      this.allergenIds = [];
    },
    closeDialog() {
      this.clear();
      foodMenusModule.setCreatingDate(null);
    },
    openCreateCategoryDialog() {
      foodMenusModule.setIsCreateCategoryDialogOpen(true);
    },
    formatClassNames(classes: NurseryClassResponse[]) {
      if (!classes || classes.length === 0) return '';
      return classes.map((c) => c.className).filter(Boolean).join(', ');
    }
  }
});
</script>

<style lang="scss" scoped>
.category-header {
  padding: 0 8px;
  font-size: 14px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
}

.category-table-headers {
  display: flex;
  align-items: center;
  padding: 4px;
  height: 30px;
  border-bottom: solid 1px #e0e0e0;
}

.category-table-header {
  font-size: 12px;
  font-weight: bold;
}

.category-table-row {
  display: flex;
  align-items: center;
  height: 40px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}

.category-table-item {
  font-size: 14px;
}

.col-checkbox {
  width: 40px;
  padding-left: 8px;
}

.col-name {
  width: 160px;
}

.col-classes {
  flex: 1;
}
</style>